import { useToast } from "@ligo/layout-design-core";
import { Errors } from "classes";
import {
  BaseProviderType,
  DSQueueOptModel,
  PermissionProviderType,
} from "models";
import { useProfile } from "providers";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import * as ListService from "services/list";
import { aytyFormatError, sortBy } from "utils";

type DSListContextType = {
  loading: boolean;
  dsQueueOpts?: DSQueueOptModel[];
};

type DSListType = {
  pDeToken?: string;
  isEmbed?: boolean;
};

type DSListPermissionProviderType = PermissionProviderType & DSListType;

type DSListProviderType = BaseProviderType & DSListType;

const DSListContext = createContext({} as DSListContextType);

const Provider = ({ children, pDeToken, isEmbed }: DSListProviderType) => {
  const [loading, setLoading] = useState(true);
  const [dsQueueOpts, setDSQueueOpts] = useState<DSQueueOptModel[]>();
  const { user, isSagoDS } = useProfile();
  const { error, warning } = useToast();
  const { t } = useTranslation();

  const errorsResolver = useMemo(
    () => new Errors({ error, warning }, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const hasPermission = useMemo(
    () => (isEmbed && pDeToken) || (isSagoDS && user?.hasAccessDashboard),
    [isEmbed, pDeToken, isSagoDS, user],
  );

  const getDSLists = useCallback(async () => {
    if (hasPermission) {
      await Promise.all([
        ListService.getDSQueueOpts(pDeToken ? { pDeToken } : {}).then(
          ({ data }) => {
            if (data.idReturnAPI > 0)
              setDSQueueOpts(sortBy(data.queueList, "NmQueue"));
            else warning({ description: t(aytyFormatError(data)) });
          },
        ),
      ]).catch(errorsResolver.defaultError);
    }

    setLoading(false);
  }, [hasPermission, pDeToken, errorsResolver, warning, t]);

  useEffect(() => {
    getDSLists();
  }, [getDSLists]);

  return (
    <DSListContext.Provider
      value={{
        loading,
        dsQueueOpts,
      }}
    >
      {children}
    </DSListContext.Provider>
  );
};

export const DSListProvider = ({
  hasPermission = true,
  ...args
}: DSListPermissionProviderType) => {
  if (!hasPermission) return <>{args.children}</>;

  return <Provider {...args} />;
};

export const useDSList = () => useContext(DSListContext);
